import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { recalculateHoursForWorkAgreement } from 'RestAPIs/admin';

import { showSnackbar } from 'components/shared';

import { employmentHoursKeys } from './useEmploymentHoursData';

type UseRecalculateEmploymentHoursParams = {
  workAgreementId: number | null;
  employeeId: number | null;
  year?: number;
};

/**
 * Recalculates hours for one work agreement, then refetches.
 *
 * Two Dojo defects disappear here:
 * - the failure handler at `EmployeeWorkAgreementsHours.js:395` was neither an arrow nor bound, so
 *   `this.messageBox` threw a TypeError and the user saw nothing on failure;
 * - the hours-correction path refetched twice (`:440-445` chained a refresh onto
 *   `_recalculateHours`, which already refreshed at `:392-394`) while the year-correction path
 *   refetched once. Invalidating here gives both callers one consistent refresh.
 */
export const useRecalculateEmploymentHours = ({
  workAgreementId,
  employeeId,
  year
}: UseRecalculateEmploymentHoursParams) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'employmentHours' });
  const queryClient = useQueryClient();

  return useMutation(
    () =>
      recalculateHoursForWorkAgreement(
        year as number,
        workAgreementId as number
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          employmentHoursKeys.hours(employeeId, workAgreementId, year)
        );
        queryClient.invalidateQueries(
          employmentHoursKeys.claYear(employeeId, workAgreementId, year)
        );
        showSnackbar({ severity: 'success', message: t('recalculateSuccess') });
      },
      onError: () =>
        showSnackbar({ severity: 'error', message: t('recalculateError') })
    }
  );
};
